/**
 * useAssetGenerationWebSocket Hook
 * 
 * React hook for subscribing to asset generation status updates via WebSocket.
 * Provides real-time updates for asset image generation progress.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { TaskType, MessageType } from '../services/websocket/types';
import { AssetGenerationStatus, AssetType } from '@packages/global';

/**
 * Asset generation progress data from WebSocket
 */
export interface AssetGenerationProgressData {
  assetId: string;
  assetName?: string;
  assetType?: AssetType;
  status: string;
  progress: number;
  imageUrl?: string;
  error?: string;
}

/**
 * Options for useAssetGenerationWebSocket hook
 */
export interface UseAssetGenerationWebSocketOptions {
  /** Whether to enable the hook, default true */
  enabled?: boolean;
  /** Callback when an asset generation completes */
  onAssetCompleted?: (assetId: string, imageUrl?: string, assetType?: AssetType) => void;
  /** Callback when an asset generation fails */
  onAssetFailed?: (assetId: string, error: string, assetType?: AssetType) => void;
  /** Callback when an asset generation progress updates */
  onProgressUpdate?: (data: AssetGenerationProgressData) => void;
}

/**
 * Return type for useAssetGenerationWebSocket hook
 */
export interface UseAssetGenerationWebSocketReturn {
  /** Whether WebSocket is connected */
  isConnected: boolean;
}

/**
 * Map WebSocket status to AssetGenerationStatus
 */
function mapStatusToAssetGenerationStatus(status: string): AssetGenerationStatus {
  switch (status.toLowerCase()) {
    case 'pending':
      return AssetGenerationStatus.PENDING;
    case 'processing':
    case 'submitted':
      return AssetGenerationStatus.GENERATING;
    case 'completed':
      return AssetGenerationStatus.COMPLETED;
    case 'failed':
      return AssetGenerationStatus.FAILED;
    default:
      return AssetGenerationStatus.IDLE;
  }
}

/**
 * Hook for subscribing to asset generation WebSocket updates
 */
export function useAssetGenerationWebSocket(
  options: UseAssetGenerationWebSocketOptions = {}
): UseAssetGenerationWebSocketReturn {
  const {
    enabled = true,
    onAssetCompleted,
    onAssetFailed,
    onProgressUpdate
  } = options;

  const { client, isConnected } = useWebSocketContext();
  const callbacksRef = useRef({ onAssetCompleted, onAssetFailed, onProgressUpdate });

  // Update callbacks ref
  useEffect(() => {
    callbacksRef.current = { onAssetCompleted, onAssetFailed, onProgressUpdate };
  }, [onAssetCompleted, onAssetFailed, onProgressUpdate]);

  // Handle task status changed messages
  const handleStatusChanged = useCallback((message: any) => {
    // Only handle asset generation tasks
    if (message.taskType !== TaskType.ASSET_GENERATION) {
      return;
    }

    console.log('[useAssetGenerationWebSocket] Raw message received:', {
      messageType: message.messageType,
      taskId: message.taskId,
      taskType: message.taskType,
      newStatus: message.newStatus,
      dataType: typeof message.data,
      data: message.data
    });

    // Parse data if it's a string (from job result)
    let data = message.data || {};
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
        console.log('[useAssetGenerationWebSocket] Parsed string data:', data);
      } catch (e) {
        console.warn('[useAssetGenerationWebSocket] Failed to parse data:', data);
        data = {};
      }
    }

    const assetId = data.assetId || message.taskId;
    const newStatus = message.newStatus?.toLowerCase() || '';

    console.log('[useAssetGenerationWebSocket] Processed status update:', {
      assetId,
      status: message.newStatus,
      imageUrl: data.imageUrl,
      dataKeys: Object.keys(data)
    });

    // Call progress update callback
    if (callbacksRef.current.onProgressUpdate) {
      callbacksRef.current.onProgressUpdate({
        assetId,
        assetName: data.assetName,
        assetType: data.assetType,
        status: message.newStatus,
        progress: data.progress || 0,
        imageUrl: data.imageUrl,
        error: data.error
      });
    }

    // Call completion/failure callbacks
    if (newStatus === 'completed') {
      if (callbacksRef.current.onAssetCompleted) {
        callbacksRef.current.onAssetCompleted(assetId, data.imageUrl, data.assetType);
      }
    } else if (newStatus === 'failed') {
      if (callbacksRef.current.onAssetFailed) {
        callbacksRef.current.onAssetFailed(assetId, data.error || '生成失败', data.assetType);
      }
    }
  }, []);

  // Handle task progress messages
  const handleProgress = useCallback((message: any) => {
    if (message.taskType !== TaskType.ASSET_GENERATION) {
      return;
    }

    const data = message.data || {};
    const assetId = data.assetId || message.taskId;

    console.log('[useAssetGenerationWebSocket] Received progress update:', {
      assetId,
      progress: message.progress,
      data
    });

    if (callbacksRef.current.onProgressUpdate) {
      callbacksRef.current.onProgressUpdate({
        assetId,
        assetName: data.assetName,
        assetType: data.assetType,
        status: data.status || 'processing',
        progress: message.progress || 0,
        imageUrl: data.imageUrl,
        error: data.error
      });
    }
  }, []);

  // 用 ref 追踪是否已订阅，避免重复订阅
  const subscribedRef = useRef(false);

  // Subscribe to asset generation task type
  useEffect(() => {
    if (!enabled || !client || !isConnected) {
      subscribedRef.current = false;
      return;
    }

    // 已订阅则只注册消息处理器，不重复 subscribe
    if (!subscribedRef.current) {
      client.subscribe({
        type: 'taskType',
        taskType: TaskType.ASSET_GENERATION
      })
        .then(() => {
          subscribedRef.current = true;
        })
        .catch((error: Error) => {
          console.warn('[useAssetGenerationWebSocket] Failed to subscribe:', error.message);
        });
    }

    // Register message handlers
    const unsubscribeStatusChanged = client.onMessage(MessageType.TASK_STATUS_CHANGED, handleStatusChanged);
    const unsubscribeProgress = client.onMessage(MessageType.TASK_PROGRESS, handleProgress);

    return () => {
      unsubscribeStatusChanged();
      unsubscribeProgress();
    };
  }, [enabled, client, isConnected, handleStatusChanged, handleProgress]);

  return {
    isConnected
  };
}
